import { FormApi, FormState } from 'informed';
import { useReduxState } from 'hooks';
import { FlexContainer } from 'components/common';
import {
  EmailMessageEditor,
  FormGroup,
  NumberStepper,
  Password,
  RadioGroup
} from 'components/common/form';
import { StatCount } from 'components/common/stats';

import type { EmailTemplateData } from 'shared-types';
import type {
  FormValuesInUsersImportCsv,
  UsersDataInUsersImportCsv
} from './UsersImportCsv';

interface Props {
  emailTemplates: EmailTemplateData[];
  formApi: FormApi;
  formState: FormState<FormValuesInUsersImportCsv>;
  isVisible: boolean;
  usersData: UsersDataInUsersImportCsv;
}

const UsersImportCsvRegistration = ({
  emailTemplates,
  formApi,
  formState,
  isVisible,
  usersData
}: Props) => {
  const { messages, serverConfig } = useReduxState();

  const validRows =
    usersData?.filter((user) => user.status === 'ok')?.length || 0;

  const updateRows =
    usersData?.filter((user) => user.status === 'update')?.length || 0;

  return (
    <div>
      <FlexContainer style={{ gap: 10 }}>
        <StatCount
          category="success-light"
          isLarge={true}
          label={messages.commonNewUsers}
          value={validRows}
        />
        <StatCount
          category="warning-light"
          isLarge={true}
          label={messages.commonUpdatedUsers}
          value={updateRows}
        />
      </FlexContainer>

      <FormGroup
        label={messages.commonRegistration}
        labelTooltip={messages.usersImportRegistrationTooltip}
        maxWidth="100%"
      >
        <RadioGroup
          field="registrationUsePassword"
          options={[
            {
              disabled: formState.values.accountType !== 'email',
              id: `import-csv-registration-use-password-true`,
              label: messages.usersRegistrationEmail,
              value: false
            },
            {
              id: `import-csv-registration-use-password-false`,
              label: messages.usersRegistrationPassword,
              value: true
            },
            {
              id: `import-csv-registration-no-password-or-email`,
              label: messages.usersRegistrationSsoOnly,
              value: undefined
            }
          ]}
        />
      </FormGroup>

      {isVisible && formState.values.registrationUsePassword ? (
        <>
          <FormGroup label={messages.commonPassword} maxWidth="100%">
            <Password
              autoComplete="new-password"
              field="password"
              formApi={formApi}
              id="import-csv-registration-password"
              placeholder={messages.commonPassword}
              required={true}
              showGenerateOption={true}
              style={{ maxWidth: 280 }}
              validateNewPassword={true}
            />
          </FormGroup>

          {!serverConfig.passwordDoNotForcePasswordChange && (
            <NumberStepper
              field="passwordExpirationInDays"
              id={`import-csv-registration-password-expiration-in-days`}
              initialValue={
                serverConfig.passwordManuallySetExpirationTimeInDays
              }
              label={messages.siteSettingsPasswordExpirationTimeInDays}
              max={100000}
              min={serverConfig.passwordManuallySetExpirationTimeInDays}
              stepValue={1}
              style={{ marginTop: 10 }}
            />
          )}
        </>
      ) : null}

      {!formState.values.registrationUsePassword &&
      formState.values.registrationUsePassword !== undefined ? (
        <EmailMessageEditor
          availableTemplates={emailTemplates}
          id="import-csv-registration-email"
          style={{ marginTop: 20 }}
        />
      ) : null}
    </div>
  );
};

export default UsersImportCsvRegistration;
